import { isStaticFunctionalZone } from '../../config';
import {
  ExerciseSessionType,
  IGymStateSessionViewModel,
  SessionSequenceEntry,
} from '../../entities';
import { DbGymRepository } from '../../persistence';
import { WarmupAndWarmdownSelector } from '../workoutselection';

export type UpdateHrMonitorSide = 'left' | 'right' | 'both' | 'neither';

interface IUpdateScreenReason {
  update: boolean;
  debugReason: string;
  reconnectHrOnSide: UpdateHrMonitorSide;
}

function userChanged(previous: IGymStateSessionViewModel, current: IGymStateSessionViewModel) {
  return previous.userProfile.uid !== current.userProfile.uid;
}

export class ScreenSession {
  public stateTime: number;

  public endTime: number;

  public leftStationInZone: IGymStateSessionViewModel;

  public rightStationInZone: IGymStateSessionViewModel;

  public audioSequenceForAllZones: SessionSequenceEntry[];

  public leftStationIsEmpty: boolean;

  public rightStationIsEmpty: boolean;

  public zoneNumber: number;

  public displayNumber: string;

  public zoneName: string;

  public nextZoneNumber: number;

  public leftRealtimeUrl: string | undefined;

  public rightRealtimeUrl: string | undefined;

  public allEventsRealtimeUrl: string;

  public gymTimeZone: string | undefined;

  public leftStationCheckedIn: boolean;

  public rightStationCheckedIn: boolean;

  constructor(
    private gymName: string,
    left: IGymStateSessionViewModel,
    right: IGymStateSessionViewModel,
    audioSequenceForAllZones: SessionSequenceEntry[],
    stateTime: number,
    endTime: number,
    allEventsRealtimeUrl: string,
    leftRealtimeUrl?: string,
    rightRealtimeUrl?: string,
  ) {
    this.leftStationInZone = left;
    this.rightStationInZone = right;
    this.audioSequenceForAllZones = audioSequenceForAllZones;
    this.stateTime = stateTime;
    this.endTime = endTime;
    this.leftStationIsEmpty = this.leftStationInZone.type === ExerciseSessionType.Empty;
    this.rightStationIsEmpty = this.rightStationInZone.type === ExerciseSessionType.Empty;
    this.leftStationCheckedIn = this.leftStationInZone.checkedIn;
    this.rightStationCheckedIn = this.rightStationInZone.checkedIn;

    this.zoneName = left.zoneName;
    this.zoneNumber = left.zoneNumber;
    this.displayNumber = left.displayNumber;
    this.nextZoneNumber = left.nextZoneNumber;
    this.leftRealtimeUrl = leftRealtimeUrl;
    this.rightRealtimeUrl = rightRealtimeUrl;
    this.allEventsRealtimeUrl = allEventsRealtimeUrl;
    this.gymTimeZone = this.getGymTimezone();
  }

  public shouldUpdateScreen(previousScreenSession?: ScreenSession): IUpdateScreenReason {
    const update = (debugReason: string, reconnectHrOnSide: UpdateHrMonitorSide = 'neither'): IUpdateScreenReason => ({
      update: true,
      debugReason,
      reconnectHrOnSide,
    });
    const doNotUpdate = (debugReason: string): IUpdateScreenReason => ({
      update: false,
      debugReason,
      reconnectHrOnSide: 'neither',
    });

    if (!previousScreenSession) {
      // more for type checking
      return update('no previous screen session');
    }
    if (previousScreenSession.isOffline() && !this.isOffline()) {
      return doNotUpdate('do not change from offline (the workout will change)');
    }
    if (!previousScreenSession.isOffline() && this.isOffline()) {
      return doNotUpdate('do not change to offline (wait for next time slice)');
    }

    const leftUserChanged = userChanged(previousScreenSession.leftStationInZone, this.leftStationInZone);
    const rightUserChanged = userChanged(previousScreenSession.rightStationInZone, this.rightStationInZone);
    if (leftUserChanged && rightUserChanged) {
      return update('both users changed', 'both');
    }
    if (leftUserChanged) {
      return update('left user changed', 'left');
    }
    if (rightUserChanged) {
      return update('right user changed', 'right');
    }
    if (previousScreenSession.leftStationInZone.checkedIn !== this.leftStationInZone.checkedIn) {
      return update('left user checked-in');
    }
    if (previousScreenSession.rightStationInZone.checkedIn !== this.rightStationInZone.checkedIn) {
      return update('right user checked-in');
    }

    return doNotUpdate('unchanged');
  }

  public isOffline(): boolean {
    return this.leftStationInZone.type === ExerciseSessionType.Offline
      || this.rightStationInZone.type === ExerciseSessionType.Offline;
  }

  public getMinsOrZonesRemainingText(userExerciseSession: IGymStateSessionViewModel, minsRemainingInWorkout: number) {
    const { amountOfStages, currentStage } = userExerciseSession;

    if (!amountOfStages) {
      return 'Offline';
    }

    if (isStaticFunctionalZone(userExerciseSession.areaCategory)) { // zones don't make sense for static, use mins
      if (minsRemainingInWorkout === 1) {
        return 'Last minute!';
      }
      const isPlural = minsRemainingInWorkout !== 1;
      const timeRemaining = `${minsRemainingInWorkout} min${isPlural ? 's' : ''} remaining`;
      return timeRemaining;
    }

    const totalNumberOfExerciseZones = amountOfStages;
    const zonesRemaining = this.getZonesRemaining(amountOfStages, currentStage);
    const warmupAndWarmdownSelector = new WarmupAndWarmdownSelector();

    const amountOfWarmdownZones = warmupAndWarmdownSelector.amountOfWarmdownZones(amountOfStages);
    const nextZoneIsWarmdown = zonesRemaining === amountOfWarmdownZones && amountOfWarmdownZones !== 0;
    const onWarmdownZone = zonesRemaining < amountOfWarmdownZones;
    if (nextZoneIsWarmdown) {
      return 'Cool down remaining';
    }

    if (onWarmdownZone) {
      return 'Last zone';
    }
    const numberOfZonesRemaining = totalNumberOfExerciseZones - currentStage;
    return `${numberOfZonesRemaining} zones remaining`;
  }

  private getZonesRemaining(amountOfStages: number, currentStage: number): number {
    return amountOfStages - currentStage;
  }

  private getGymTimezone() {
    const gym = DbGymRepository.getByName(this.gymName);
    return gym.timezone;
  }
}
